import { Component, computed } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ListingChannelComparisonService } from 'listings/services/listing-channel-comparison.service';
import { ZERO_DATE } from 'shared/constants/general-constants';
import { DateComparisonType } from 'shared/enums/dates.enum';
import { BannerViewModel, ChannelBannerBadge } from 'shared/interfaces/context-aware-sidebar.interface';
import { ListingComparisonOrchestratorService } from 'shared/services/listing-comparion-orchestrator.service';
import { compareDateWithDaysAgo } from 'shared/util/dates.utils';

@Component({
    selector: 'app-listing-comparison-banner',
    templateUrl: './listing-comparison-banner.component.html',
    styleUrl: './listing-comparison-banner.component.scss',
    imports: [TranslateModule],
})
export class ListingComparisonBannerComponent {
    protected readonly isComparisonActive = this.orchestratorService.isComparisonActive;

    private readonly isChannelSnapshotStale = computed(() => {
        const snapshot = this.channelComparisonService.activeChannelSnapshot();
        if (!snapshot?.updatedAt || snapshot.updatedAt === ZERO_DATE) {
            return false;
        }
        return compareDateWithDaysAgo(snapshot.updatedAt, 1, DateComparisonType.OLDER);
    });

    private readonly hasNewlyGeneratedSnapshot = computed(() => !!this.channelComparisonService.newlyGeneratedChannelSnapshot());

    protected readonly bannerViewModel = computed<BannerViewModel | null>(() => {
        const info = this.orchestratorService.bannerInfo();
        if (!info) {
            return null;
        }

        if (info.mode === 'version') {
            const explainerText = this.translateService.instant('listing.version.versionExplainer', {
                currentVersion: info.versionNumber,
                maxVersion: info.maxVersion,
            });
            return {
                mode: 'version',
                explainerText,
                badges: [],
            };
        }

        const explainerText = this.translateService.instant('listing.channelComparison.explainer', {
            activeSnapshotChannelName: info.channelName ?? '',
        });

        const badges: ChannelBannerBadge[] = [];
        if (this.isChannelSnapshotStale()) {
            badges.push({
                type: 'stale',
                labelKey: 'listings.channelComparison.badges.stale.label',
                tooltipKey: 'listings.channelComparison.badges.stale.tooltip',
                clickable: false,
            });
        }
        if (this.hasNewlyGeneratedSnapshot()) {
            badges.push({
                type: 'newSnapshot',
                labelKey: 'listings.channelComparison.badges.newSnapshot.label',
                tooltipKey: 'listings.channelComparison.badges.newSnapshot.tooltip',
                clickable: true,
            });
        }

        return {
            mode: 'channel',
            explainerText,
            badges,
        };
    });

    constructor(
        private orchestratorService: ListingComparisonOrchestratorService,
        private channelComparisonService: ListingChannelComparisonService,
        private translateService: TranslateService,
    ) {}

    protected handleExitComparisonClick(): void {
        this.orchestratorService.endComparison();
    }

    protected handleNewSnapshotBadgeClick(): void {
        const newlyGeneratedSnapshot = this.channelComparisonService.newlyGeneratedChannelSnapshot();
        if (!newlyGeneratedSnapshot) {
            return;
        }
        this.channelComparisonService.activeChannelSnapshot.set(newlyGeneratedSnapshot);
        this.channelComparisonService.newlyGeneratedChannelSnapshot.set(null);
    }
}
